import { Box, Grid, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import { useDriverStore } from "../../../../../store/useDriverStore";
import Input from "../../../../../components/Input";
import { getFleetBranches, updateDriverDetails } from "../../../../../services/dashboard";
import { useToast } from "../../../../../hooks/useToast";
import { toast } from "react-toastify";
import {
  OperationsDetailField,
  OperationsDetailGrid,
} from "../../../ifas/components/OperationsUI";

export default function PersonalDetails({
  isEditing,
  setIsEditing,
  registerUpdateHandler,
}: {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  registerUpdateHandler?: (fn: (() => Promise<void>) | null) => void;
}) {
  const { selectedDriver, setSelectedDriver } = useDriverStore();
  const { showInfoToast } = useToast();
  const driver = selectedDriver;
  const [isUpdating, setIsUpdating] = useState(false);
  const [fleetBranches, setFleetBranches] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    gender: "",
    licenseCode: "",
    licenseIssueDate: "",
    licenseExpiryDate: "",
    hasPdp: false,
    workShift: "",
    branchId: "",
    branchLocation: "",
    assignedVehicleNumber: "",
    assignedVehicleType: "",
  });

  useEffect(() => {
    const loadBranches = async () => {
      const response = await getFleetBranches();
      if (response?.status === 200 && Array.isArray(response.data)) {
        setFleetBranches(
          [...response.data].sort((a: any, b: any) =>
            String(a.branchName || "").localeCompare(String(b.branchName || "")),
          ),
        );
      }
    };

    void loadBranches();
  }, []);

  useEffect(() => {
    if (!driver) {
      return;
    }

    setFormData({
      firstName: driver.firstName || "",
      lastName: driver.lastName || "",
      phoneNumber: driver.phoneNumber || "",
      email: driver.email || "",
      gender: driver.gender || "",
      licenseCode: driver.licenseCode || "",
      licenseIssueDate: driver.licenseIssueDate || "",
      licenseExpiryDate: driver.licenseExpiryDate || "",
      hasPdp: Boolean(driver.hasPdp),
      workShift: driver.workShift || "",
      branchId: driver.branchId || "",
      branchLocation: driver.branchName || driver.branchLocation || "",
      assignedVehicleNumber: driver.assignedVehicleNumber || "",
      assignedVehicleType: driver.assignedVehicleType || "",
    });
  }, [driver]);

  const handleFieldChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === "hasPdp" ? event.target.value === "true" : event.target.value;

      if (field === "branchId") {
        const selectedBranch = fleetBranches.find(
          (branch: any) => String(branch.id) === String(value),
        );
        setFormData((prev) => ({
          ...prev,
          branchId: String(value),
          branchLocation: selectedBranch?.branchName || "",
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleUpdate = async () => {
    if (!driver?.driverID) {
      showInfoToast("Unable to identify the selected driver");
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        ...formData,
        branchId: formData.branchId || undefined,
        branchLocation: formData.branchLocation || undefined,
      };
      const response = await updateDriverDetails(driver.driverID, payload);

      if (response?.status === 200 || response?.status === 201) {
        const selectedBranch = fleetBranches.find(
          (branch: any) => String(branch.id) === String(formData.branchId),
        );
        setSelectedDriver({
          ...driver,
          ...payload,
          branchName: selectedBranch?.branchName || driver.branchName,
          branchLocation: selectedBranch?.branchName || formData.branchLocation,
        });
        setIsEditing(false);
        toast.success("Driver details updated successfully");
      } else {
        showInfoToast(response?.data?.message || "Failed to update driver");
      }
    } catch (error) {
      showInfoToast("Failed to update driver");
    } finally {
      setIsUpdating(false);
    }
  };

useEffect(() => {
  registerUpdateHandler?.(isEditing ? handleUpdate : null);

  return () => {
    registerUpdateHandler?.(null);
  };
}, [isEditing, driver?.driverID, formData, fleetBranches]);

  const readOnlyBackground = isEditing ? "#ffffff" : "#f5f5f5";

  if (!isEditing) {
    return (
      <OperationsDetailGrid>
        <OperationsDetailField label="Driver Number" value={driver?.driverNumber} />
        <OperationsDetailField label="First Name" value={driver?.firstName} />
        <OperationsDetailField label="Last Name" value={driver?.lastName} />
        <OperationsDetailField label="Phone Number" value={driver?.phoneNumber} />
        <OperationsDetailField label="Email" value={driver?.email} />
        <OperationsDetailField label="License Code" value={driver?.licenseCode} />
        <OperationsDetailField label="Gender" value={driver?.gender} />
        <OperationsDetailField label="Vehicle Type" value={driver?.assignedVehicleType} />
        <OperationsDetailField label="Driver Status" value={driver?.driverStatus} />
        <OperationsDetailField label="Branch Location" value={driver?.branchName || driver?.branchLocation} />
        <OperationsDetailField label="Current Case" value={driver?.currentCaseNumber} />
        <OperationsDetailField
          label="Allocation Number"
          value={driver?.currentAllocationNumber}
        />
        <OperationsDetailField
          label="Current Body Removal"
          value={driver?.currentBodyRemovalNumber}
        />
        <OperationsDetailField
          label="Dispatch Status"
          value={driver?.currentBodyRemovalStatus}
        />
        <OperationsDetailField label="Work Shift" value={driver?.workShift} />
        <OperationsDetailField label="License Issue Date" value={driver?.licenseIssueDate} />
        <OperationsDetailField label="License Expiry Date" value={driver?.licenseExpiryDate} />
      </OperationsDetailGrid>
    );
  }

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled
          value={driver?.driverNumber || ""}
          height={40}
          width="100%"
          label="Driver Number"
          backgroundColor="background.paper"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.firstName}
          onChange={handleFieldChange("firstName")}
          height={40}
          width="100%"
          label="First Name"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.lastName}
          onChange={handleFieldChange("lastName")}
          height={40}
          width="100%"
          label="Last Name"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.email}
          onChange={handleFieldChange("email")}
          height={40}
          width="100%"
          label="Email"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.phoneNumber}
          onChange={handleFieldChange("phoneNumber")}
          height={40}
          width="100%"
          label="Phone Number"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          select
          value={formData.licenseCode}
          onChange={handleFieldChange("licenseCode")}
          height={40}
          width="100%"
          label="License Code"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="CODE_8">Code 8</MenuItem>
          <MenuItem value="CODE_10">Code 10</MenuItem>
          <MenuItem value="CODE_14">Code 14</MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.gender}
          onChange={handleFieldChange("gender")}
          height={40}
          width="100%"
          label="Gender"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="MALE">Male</MenuItem>
          <MenuItem value="FEMALE">Female</MenuItem>
        </Input>
      </Grid>
       <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled
          value={formData.assignedVehicleType}
          height={40}
          width="100%"
          label="Vehicle Type"
          backgroundColor="background.paper"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled
          value={driver?.driverStatus}
          height={40}
          width="100%"
          label="Driver Status"
          backgroundColor="background.paper"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.branchId}
          onChange={handleFieldChange("branchId")}
          height={40}
          width="100%"
          label="Branch"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Select branch</MenuItem>
          {fleetBranches.map((branch: any) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.branchName}
              {branch.province ? ` - ${branch.province}` : ""}
            </MenuItem>
          ))}
        </Input>
      </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.workShift}
          onChange={handleFieldChange("workShift")}
          height={40}
          width="100%"
          label="Work Shift"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="MORNING_SHIFT">Morning Shift</MenuItem>
          <MenuItem value="MID_DAY_SHIFT">Afternoon Shift</MenuItem>
          <MenuItem value="NIGHT_SHIFT">Night Shift</MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={String(formData.hasPdp)}
          onChange={handleFieldChange("hasPdp")}
          height={40}
          width="100%"
          label="Has PDP"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </Input>
      </Grid>
       <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.licenseIssueDate}
          onChange={handleFieldChange("licenseIssueDate")}
          type="date"
          height={40}
          width="100%"
          label="License Issue Date"
          backgroundColor={readOnlyBackground}
        />
      </Grid>

       <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.licenseExpiryDate}
          onChange={handleFieldChange("licenseExpiryDate")}
          type="date"
          height={40}
          width="100%"
          label="License Expiry Date"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      {/* Update action is rendered in the page header action bar for consistency */}


      {/* {comments?.length > 0 && (
        <Grid size={12} mt={3}>
          {comments.map((comment: any, index: number) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: "#F7F7F7",
                boxShadow: "none",
                borderRadius: "6px",
                mb: 2,
                border: "1px solid #E0E0E0",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    {comment.commentedByName}{" "}
                    <Typography
                      component="span"
                      fontWeight={600}
                      color="#EC6E00"
                    >
                      - {comment.role_at_time_of_comment?.replace(/_/g, " ").toUpperCase()}
                    </Typography>
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {new Date(comment.created_on).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    | {formatDate(comment.created_on)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={15} color="#333" lineHeight={1.6}>
                  {comment.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      )}
      {isAssigned && claimStatus !== "PAID" && (
        <Grid size={{ md: 12 }} mt={3}>
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={18}
            sx={{ display: "block", textAlign: "left" }}
          >
            Comment
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            disabled={isEditable}
            value={policyComment}
            onChange={onPolicyCommentChange}
          />
        </Grid>
      )}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>

          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog> */}
    </Grid>
  );
}
